import { useState, useMemo } from 'react';
import {
  Skeleton,
  Select,
  SelectItem,
  Table,
  TableHeader,
  TableColumn,
  TableBody,
  TableRow,
  TableCell,
} from '@nextui-org/react';
import {
  PROGRAM_UNIT_LABELS,
  buildCommittedMatrix,
  commitmentsAt,
  formatProgramQuantity,
  shipmentWeek,
  shippedAmount,
} from 'common/src/utils/commercialProgram';
import { useTranslation } from '../../../../common/src/hooks/useTranslation';
import { useCommercialProgram } from '../../pages/commercial-plan/useCommercialProgram';

/**
 * Cumplimiento por cliente del Programa Comercial, en la unidad y con la
 * fecha base del programa (misma lógica que la página del programa).
 */
const SeasonComplianceWidget = () => {
  const { t } = useTranslation('dashboard');
  const { t: tc } = useTranslation('commitments');
  const { loading, program, rows, receivers, shipments, receiverIdOf, weeks, cutoffIndex } =
    useCommercialProgram();
  const [complianceMode, setComplianceMode] = useState<'toDate' | 'season'>(
    'toDate'
  );

  const effectiveCutoffIndex =
    complianceMode === 'season' ? weeks.length - 1 : cutoffIndex;

  const clientSummaryData = useMemo(() => {
    if (!program) return [];
    const committed = buildCommittedMatrix(commitmentsAt(rows), program, program.unit);
    const shipped = new Map<number, number>();
    for (const shipment of shipments) {
      const receiverId = receiverIdOf(shipment);
      if (!receiverId) continue;
      const idx = weeks.indexOf(shipmentWeek(shipment, program.date_basis) ?? -1);
      if (idx < 0 || idx > effectiveCutoffIndex) continue;
      shipped.set(
        receiverId,
        (shipped.get(receiverId) || 0) + shippedAmount(shipment, program.unit, program)
      );
    }
    return receivers
      .map((r) => {
        const comprometido = weeks
          .slice(0, Math.max(0, effectiveCutoffIndex + 1))
          .reduce((sum, w) => sum + (committed.get(r.id)?.get(w) || 0), 0);
        const enviados = shipped.get(r.id) || 0;
        return {
          cliente: r.name,
          comprometido,
          enviados,
          cumplimiento: comprometido > 0 ? (enviados / comprometido) * 100 : null,
          saldoDebido: comprometido - enviados,
        };
      })
      .filter((row) => row.comprometido > 0 || row.enviados > 0);
  }, [program, rows, shipments, receiverIdOf, receivers, weeks, effectiveCutoffIndex]);

  const totals = useMemo(() => {
    const comprometido = clientSummaryData.reduce((sum, row) => sum + row.comprometido, 0);
    const enviados = clientSummaryData.reduce((sum, row) => sum + row.enviados, 0);
    return {
      comprometido,
      enviados,
      cumplimiento: comprometido > 0 ? (enviados / comprometido) * 100 : null,
      saldo: comprometido - enviados,
    };
  }, [clientSummaryData]);

  if (loading) {
    return (
      <div className='space-y-4'>
        <Skeleton className='h-8 w-1/3 rounded-lg' />
        <Skeleton className='h-10 w-full rounded-lg' />
        <Skeleton className='h-64 w-full rounded-lg' />
      </div>
    );
  }

  if (!program) {
    return (
      <div className='text-center p-8'>
        <p className='text-default-400'>{t('Aún no hay programa comercial para esta temporada')}</p>
        <a href='/commercial-plan/detail' className='text-sm text-primary underline'>
          {t('Crear programa comercial')}
        </a>
      </div>
    );
  }

  if (receivers.length === 0) {
    return (
      <div className='text-center p-8'>
        <p className='text-default-400'>{t('No hay clientes configurados')}</p>
      </div>
    );
  }

  const fmt = (value: number) => formatProgramQuantity(value, program.unit);

  const getCumplimientoColor = (cumplimiento: number | null) => {
    if (cumplimiento == null) return 'text-default-400';
    if (cumplimiento >= 100) return 'text-green-600';
    if (cumplimiento >= 80) return 'text-yellow-600';
    return 'text-red-600';
  };

  return (
    <div className='h-full flex flex-col'>
      {/* Header - Fixed */}
      <div className='flex-shrink-0 flex flex-col md:flex-row justify-between items-start  mb-4 gap-4'>
        <div className='flex flex-col'>
          <h3 className='text-lg font-bold'>{t('Despachos')}</h3>
          <p className='text-xs text-default-500'>{tc(PROGRAM_UNIT_LABELS[program.unit])}</p>
        </div>

        <Select
          label={t('Cumplimiento')}
          selectedKeys={complianceMode ? [complianceMode] : []}
          onChange={(e) =>
            setComplianceMode(
              (e.target.value as 'toDate' | 'season') || 'toDate'
            )
          }
          className='w-full md:w-48'
          size='sm'
          aria-label='Selecciona tipo de cumplimiento'
        >
          <SelectItem key='toDate' value='toDate'>
            {t('A la fecha')}
          </SelectItem>
          <SelectItem key='season' value='season'>
            {t('Por temporada')}
          </SelectItem>
        </Select>
      </div>

      {/* Table with scrollable content */}
      <div className='flex-1 overflow-y-auto min-h-0'>
        <Table
          removeWrapper
          aria-label='Tabla de cumplimiento por cliente'
          classNames={{
            th: 'text-xs sticky top-0 z-10 bg-default-100 shadow-sm',
            td: 'text-sm',
          }}
        >
          <TableHeader>
            <TableColumn>{t('CLIENTE')}</TableColumn>
            <TableColumn className='text-right'>{t('COMPROM.')}</TableColumn>
            <TableColumn className='text-right'>{t('ENVIADOS')}</TableColumn>
            <TableColumn className='text-right'>{t('%')}</TableColumn>
            <TableColumn className='text-right'>{t('SALDO')}</TableColumn>
          </TableHeader>
          <TableBody>
            {[
              {
                cliente: t('TOTAL'),
                comprometido: totals.comprometido,
                enviados: totals.enviados,
                cumplimiento: totals.cumplimiento,
                saldoDebido: totals.saldo,
              },
              ...clientSummaryData,
            ].map((row, idx) => {
              const isTotal = row.cliente === t('TOTAL');
              return (
                <TableRow key={idx}>
                  <TableCell className={isTotal ? 'font-bold' : ''}>
                    {row.cliente}
                  </TableCell>
                  <TableCell
                    className={`text-right ${isTotal ? 'font-bold' : ''}`}
                  >
                    {fmt(row.comprometido)}
                  </TableCell>
                  <TableCell
                    className={`text-right ${isTotal ? 'font-bold' : ''}`}
                  >
                    {fmt(row.enviados)}
                  </TableCell>
                  <TableCell
                    className={`text-right font-semibold ${getCumplimientoColor(row.cumplimiento)}`}
                  >
                    {row.cumplimiento == null ? '–' : `${Math.round(row.cumplimiento)}%`}
                  </TableCell>
                  <TableCell
                    className={`text-right ${isTotal ? 'font-bold' : ''}`}
                  >
                    {fmt(row.saldoDebido)}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

export default SeasonComplianceWidget;
